import {useCallback, useEffect, useReducer, useRef, useState, type ComponentRef, type PropsWithChildren} from 'react';
import {createPortal} from 'react-dom';
import {AnimatePresence, motion} from 'framer-motion';
import {useNode} from '@craftjs/core';
import type {MeshType} from 'api/generated';
import {useProjectStore} from 'pages';
import {useClickOutsideOf, useEditorStyle} from 'hooks';
import {classNames} from 'utils';
import {imgNone} from 'assets';

import EditorInspectorLauncher from './Editor.Inspector.Launcher';

const sanitizedMeshKeys = ['type', 'uid', 'name', 'fileFormat', 'fileUrl', 'fileUsdzUrl', 'imageUrl'] as const;
type AssignedMeshType = Pick<MeshType, (typeof sanitizedMeshKeys)[number]>;

interface IEditorContentLauncherProps {
  mobileStyle?: React.CSSProperties;
  assignedMeshes?: AssignedMeshType[];
}

const EditorContentLauncher = ({mobileStyle, assignedMeshes, ...props}: IEditorContentLauncherProps) => {
  const {
    connectors: {connect, drag},
    actions: {setProp},
    selected,
    hovered,
  } = useNode(node => ({
    selected: node.events.selected,
    hovered: node.events.hovered,
  }));

  const ref = useCallback((node: HTMLImageElement) => {
    if (node) {
      connect(drag(node));
    }
  }, []);

  const meshes = useProjectStore(s => s.meshes) ?? [];
  useEffect(() => {
    const newAssignedMeshes = meshes.filter(m => m.assigned);
    const toAssignedMeshes = newAssignedMeshes.map(m => {
      const entries = Object.entries(m).filter(([k]) => sanitizedMeshKeys.includes(k as keyof AssignedMeshType));
      return Object.fromEntries(entries) as AssignedMeshType;
    });
    setProp((prop: {assignedMeshes: AssignedMeshType[]}) => (prop.assignedMeshes = toAssignedMeshes));
  }, [meshes, setProp]);

  const {device, style: editorStyle} = useEditorStyle();
  const style = device === 'mobile' ? editorStyle : {...editorStyle, display: 'none'};

  const [showModal, toggleModal] = useReducer(s => !s, false);
  const [selectedMesh, setSelectedMesh] = useState<AssignedMeshType>();

  const modalContainerRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  useClickOutsideOf(
    modalContainerRef,
    useCallback(e => {
      const clickedOnBackdrop = backdropRef.current?.contains(e.target as Node | null);
      if (clickedOnBackdrop) {
        toggleModal();
      }
    }, []),
    showModal,
  );

  const editorRootId = useProjectStore(s => s.editorRootId);
  const root = document.getElementById(editorRootId);

  return (
    <div ref={ref} className="relative">
      <div
        className={classNames(
          'absolute inset-0 pointer-events-none',
          hovered ? 'shadow-[inset_0_0_0_2px_#34bb78]' : selected ? 'shadow-[inset_0_0_0_2px_#acbdd4]' : '',
        )}></div>
      <button type="button" {...props} style={style} className="cursor-pointer" onClick={toggleModal}>
        Start AR
      </button>
      {!!root &&
        device === 'mobile' &&
        createPortal(
          <Modal isVisible={showModal} handlePressOutside={toggleModal}>
            <h2 className="text-xl text-black font-medium">Select model</h2>
            <div className="max-h-80 grid grid-cols-2 gap-3 my-6 overflow-y-scroll no-scrollbar">
              {assignedMeshes?.map(mesh => {
                return (
                  <ARButtonModelSelect
                    key={mesh.uid}
                    img={mesh.imageUrl || imgNone}
                    label={mesh.name}
                    onClick={() => setSelectedMesh(mesh)}
                    selected={selectedMesh?.uid === mesh.uid}
                  />
                );
              })}
            </div>
            <div className="flex justify-center items-center">
              <button
                type="button"
                {...props}
                className={classNames(
                  'w-full p-2 rounded-md font-medium text-lg text-white cursor-pointer transition duration-200',
                  !!selectedMesh && 'active:scale-95',
                )}
                style={{backgroundColor: selectedMesh ? style.backgroundColor : '#8fa1b9'}}
                disabled={!selectedMesh}>
                Start AR
              </button>
            </div>
          </Modal>,
          root,
        )}
    </div>
  );
};

interface IModalProps {
  isVisible: boolean;
  handlePressOutside: () => void;
}

const Modal = ({children, isVisible, handlePressOutside}: PropsWithChildren<IModalProps>) => {
  const ref = useRef<ComponentRef<typeof motion.div>>(null);
  const backdropRef = useRef<ComponentRef<'div'>>(null);
  useClickOutsideOf(
    ref,
    useCallback(e => {
      const clickedOnBackdrop = backdropRef.current?.contains(e.target as Node | null);
      if (clickedOnBackdrop) {
        handlePressOutside();
      }
    }, []),
    isVisible,
  );
  return (
    <AnimatePresence>
      {isVisible && (
        <div className="absolute inset-0 z-10 flex items-center justify-center px-2 pb-14">
          <div ref={backdropRef} className="absolute inset-0 bg-black opacity-50"></div>
          <motion.div
            ref={ref}
            initial={{opacity: 0, translateY: 10}}
            animate={{opacity: 1, translateY: 0}}
            exit={{opacity: 0, translateY: -10}}
            className="relative max-w-md w-full">
            <div className="w-full bg-white p-4 rounded-md">{children}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

interface IARButtonModelSelect {
  img: string;
  label: string;
  onClick: () => void;
  selected?: boolean;
}

const ARButtonModelSelect = ({img, label, onClick, selected = false}: IARButtonModelSelect) => {
  return (
    <div className="col-span-1 flex flex-col items-center gap-2">
      <img
        src={img}
        alt={label}
        className={classNames('aspect-square bg-slate-200 object-cover rounded-md border-2', selected ? 'border-[#34bb78]' : 'border-transparent')}
        onClick={onClick}
      />
      <p className="text-lg text-center text-slate-600 line-clamp-2 capitalize">{label.split('_').join(' ')}</p>
    </div>
  );
};

EditorContentLauncher.craft = {
  displayName: 'Launcher',
  custom: {
    icon: 'RocketLaunchIcon',
  },
  props: {
    assignedMeshes: [],
    style: {
      color: 'white',
      fontWeight: '500',
      backgroundColor: 'rgba(52, 187, 120, 1)',
      borderRadius: '30px',
      padding: '12px 32px 12px 32px',
      margin: '0 0 0 0',
    },
    mobileStyle: {},
  },
  related: {
    settings: EditorInspectorLauncher,
  },
};

export default EditorContentLauncher;
